import type { EntityBoardItemInteraction } from '../context';

/**
 * What a single click on a board row is for.
 *
 * `panel`  — focus the row so the aside renders it.
 * `navigate` — the mount's explicit destination.
 * `open`   — the record's own edit surface, because nothing else on this board would show it.
 * `none`   — the click is inert (bulk-selection boards, unopenable rows).
 */
export type BoardRowActivation = 'panel' | 'navigate' | 'open' | 'none';

export interface BoardRowActivationInput {
  /** True only when the aside will actually render — see `$panelDrivesClick`, never `$panelKind`. */
  panelDrivesClick: boolean;
  interaction: Pick<EntityBoardItemInteraction, 'navigate'> | null | undefined;
  /**
   * Whether the board entity declares an edit operation a click could open.
   *
   * A thunk, not a boolean: it costs two manifest lookups and only the last branch needs the answer,
   * so a panel or navigate click must not pay for it on every row.
   */
  canOpenRecord: () => boolean;
}

/**
 * The panel wins wherever one will render, because the click was made to read the record beside the
 * collection and navigating away undoes that. `navigate` is the mount's answer for a board without
 * one.
 *
 * `open` is the case that was missing: a preview board whose preview is currently HIDDEN declares no
 * `navigate` — it never needed one, the aside was always there — so the click had nowhere to land
 * and silently did nothing. Quote Pro in Table mode with Show Preview off is exactly that board.
 */
export function resolveBoardRowActivation(input: BoardRowActivationInput): BoardRowActivation {

  if (input.panelDrivesClick) return 'panel';
  if (input.interaction?.navigate) return 'navigate';
  return input.canOpenRecord() ? 'open' : 'none';
}
